import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { type ShiftDetails, type CloseShiftData } from '../api/shifts.api';

interface FacturaData {
  // Encabezado
  nombreParqueadero: string;
  nit: string;
  direccion: string;
  telefono: string;
  fechaEmision: string;
  numeroFactura: string;
  operador: string;

  // Datos del vehículo
  placa: string;
  tipoVehiculo: string;
  cliente?: string;
  contrato?: string;
  espacioAsignado?: string;

  // Detalles del tiempo
  horaEntrada: string;
  horaSalida: string;
  tiempoTotal: string;

  // Detalles de tarifación
  tarifaAplicada: string;
  valorUnidad: number;
  unidadesCobradas: string;
  subtotal: number;
  iva: number;
  otrosCargos?: number;
  descuentos?: number;

  // Total
  total: number;
  metodoPago: string;
  numeroComprobante: string;

  // Auditoría
  idMovimiento: string;
  idBoleto: string;
  camara?: string;
  operacion: string;
}

export async function generarFacturaPDF(data: FacturaData, ancho: 50 | 80): Promise<void> {
  const anchoMM = ancho;
  const margen = 3;
  const anchoUtil = anchoMM - (margen * 2);

  // Crear PDF con altura automática
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [anchoMM, 297] // Altura inicial, se ajustará después
  });

  let y = margen;
  const lineHeight = 4;
  const fontSize = ancho === 50 ? 7 : 9;
  const fontSizeSmall = ancho === 50 ? 6 : 7;
  const fontSizeLarge = ancho === 50 ? 9 : 11;

  pdf.setFont('helvetica');

  // ENCABEZADO
  pdf.setFontSize(fontSizeLarge);
  pdf.setFont('helvetica', 'bold');
  const empresaLines = pdf.splitTextToSize(data.nombreParqueadero, anchoUtil);
  empresaLines.forEach((line: string) => {
    pdf.text(line, anchoMM / 2, y, { align: 'center' });
    y += lineHeight;
  });

  pdf.setFontSize(fontSizeSmall);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`NIT: ${data.nit}`, anchoMM / 2, y, { align: 'center' });
  y += lineHeight;

  const direccionLines = pdf.splitTextToSize(data.direccion, anchoUtil);
  direccionLines.forEach((line: string) => {
    pdf.text(line, anchoMM / 2, y, { align: 'center' });
    y += lineHeight;
  });

  pdf.text(`Tel: ${data.telefono}`, anchoMM / 2, y, { align: 'center' });
  y += lineHeight + 2;

  // Línea separadora
  pdf.setLineWidth(0.1);
  pdf.line(margen, y, anchoMM - margen, y);
  y += 3;

  pdf.setFontSize(fontSize);
  pdf.setFont('helvetica', 'bold');
  pdf.text('FACTURA DE PARQUEADERO', anchoMM / 2, y, { align: 'center' });
  y += lineHeight;

  pdf.setFont('helvetica', 'normal');
  pdf.text(`No. ${data.numeroFactura}`, anchoMM / 2, y, { align: 'center' });
  y += lineHeight;
  pdf.text(`Fecha: ${data.fechaEmision}`, anchoMM / 2, y, { align: 'center' });
  y += lineHeight;
  pdf.text(`Operador: ${data.operador}`, anchoMM / 2, y, { align: 'center' });
  y += lineHeight + 2;

  // Línea separadora
  pdf.line(margen, y, anchoMM - margen, y);
  y += 3;

  // DATOS DEL VEHÍCULO
  pdf.setFont('helvetica', 'bold');
  pdf.text('DATOS DEL VEHÍCULO', margen, y);
  y += lineHeight;

  pdf.setFont('helvetica', 'normal');
  pdf.text(`Placa: ${data.placa}`, margen, y);
  y += lineHeight;
  pdf.text(`Tipo: ${data.tipoVehiculo}`, margen, y);
  y += lineHeight;

  if (data.cliente) {
    pdf.text(`Cliente: ${data.cliente}`, margen, y);
    y += lineHeight;
  }

  if (data.contrato) {
    pdf.text(`Contrato: ${data.contrato}`, margen, y);
    y += lineHeight;
  }

  if (data.espacioAsignado) {
    pdf.text(`Espacio: ${data.espacioAsignado}`, margen, y);
    y += lineHeight;
  }

  y += 2;

  // Línea separadora
  pdf.line(margen, y, anchoMM - margen, y);
  y += 3;

  // DETALLES DEL TIEMPO
  pdf.setFont('helvetica', 'bold');
  pdf.text('TIEMPO DE ESTADÍA', margen, y);
  y += lineHeight;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(fontSizeSmall);
  pdf.text(`Entrada: ${data.horaEntrada}`, margen, y);
  y += lineHeight;
  pdf.text(`Salida: ${data.horaSalida}`, margen, y);
  y += lineHeight;

  pdf.setFontSize(fontSize);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Tiempo total: ${data.tiempoTotal}`, margen, y);
  y += lineHeight + 2;

  // Línea separadora
  pdf.setFont('helvetica', 'normal');
  pdf.line(margen, y, anchoMM - margen, y);
  y += 3;

  // DETALLES DE TARIFACIÓN
  pdf.setFont('helvetica', 'bold');
  pdf.text('DETALLE DE COBRO', margen, y);
  y += lineHeight;

  pdf.setFont('helvetica', 'normal');
  pdf.text(`Tarifa: ${data.tarifaAplicada}`, margen, y);
  y += lineHeight;
  pdf.text(`Valor unidad: $${data.valorUnidad.toLocaleString('es-CO')}`, margen, y);
  y += lineHeight;
  pdf.text(`Unidades: ${data.unidadesCobradas}`, margen, y);
  y += lineHeight + 1;

  // Subtotal
  pdf.text('Subtotal:', margen, y);
  pdf.text(`$${data.subtotal.toLocaleString('es-CO')}`, anchoMM - margen, y, { align: 'right' });
  y += lineHeight;

  // IVA
  if (data.iva > 0) {
    pdf.text('IVA:', margen, y);
    pdf.text(`$${data.iva.toLocaleString('es-CO')}`, anchoMM - margen, y, { align: 'right' });
    y += lineHeight;
  }

  // Otros cargos
  if (data.otrosCargos && data.otrosCargos > 0) {
    pdf.text('Otros cargos:', margen, y);
    pdf.text(`$${data.otrosCargos.toLocaleString('es-CO')}`, anchoMM - margen, y, { align: 'right' });
    y += lineHeight;
  }

  // Descuentos
  if (data.descuentos && data.descuentos > 0) {
    pdf.text('Descuentos:', margen, y);
    pdf.text(`-$${data.descuentos.toLocaleString('es-CO')}`, anchoMM - margen, y, { align: 'right' });
    y += lineHeight;
  }

  y += 1;

  // Línea separadora
  pdf.setLineWidth(0.3);
  pdf.line(margen, y, anchoMM - margen, y);
  y += 3;

  // TOTAL
  pdf.setFontSize(fontSizeLarge);
  pdf.setFont('helvetica', 'bold');
  pdf.text('TOTAL:', margen, y);
  pdf.text(`$${data.total.toLocaleString('es-CO')}`, anchoMM - margen, y, { align: 'right' });
  y += lineHeight + 2;

  pdf.setFontSize(fontSize);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Método de pago: ${data.metodoPago}`, margen, y);
  y += lineHeight;
  pdf.text(`Comprobante: ${data.numeroComprobante}`, margen, y);
  y += lineHeight + 2;

  // Línea separadora
  pdf.setLineWidth(0.1);
  pdf.line(margen, y, anchoMM - margen, y);
  y += 3;

  // CÓDIGO QR
  const qrData = JSON.stringify({
    factura: data.numeroFactura,
    placa: data.placa,
    total: data.total,
    fecha: data.fechaEmision,
    id: data.idMovimiento
  });

  try {
    const qrDataUrl = await QRCode.toDataURL(qrData, {
      width: 200,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });

    const qrSize = ancho === 50 ? 25 : 35;
    const qrX = (anchoMM - qrSize) / 2;

    pdf.addImage(qrDataUrl, 'PNG', qrX, y, qrSize, qrSize);
    y += qrSize + 3;

    pdf.setFontSize(fontSizeSmall);
    pdf.text('Escanea para validar', anchoMM / 2, y, { align: 'center' });
    y += lineHeight + 2;
  } catch (error) {
    console.error('Error generando QR:', error);
  }

  // Línea separadora
  pdf.line(margen, y, anchoMM - margen, y);
  y += 3;

  // INFORMACIÓN DE AUDITORÍA
  pdf.setFontSize(fontSizeSmall);
  pdf.setFont('helvetica', 'normal');
  pdf.text('INFORMACIÓN DE AUDITORÍA', margen, y);
  y += lineHeight;

  pdf.text(`ID Movimiento: ${data.idMovimiento}`, margen, y);
  y += lineHeight;
  pdf.text(`ID Boleto: ${data.idBoleto}`, margen, y);
  y += lineHeight;

  if (data.camara) {
    pdf.text(`Cámara/LPR: ${data.camara}`, margen, y);
    y += lineHeight;
  }

  pdf.text(`Operación: ${data.operacion}`, margen, y);
  y += lineHeight + 3;

  // Mensaje final
  pdf.setFontSize(fontSizeSmall);
  pdf.text('Gracias por su visita', anchoMM / 2, y, { align: 'center' });
  y += lineHeight;
  pdf.text('¡Vuelva pronto!', anchoMM / 2, y, { align: 'center' });
  y += margen;

  // Copiar contenido al PDF final
  const pageData = pdf.output('datauristring');
  const img = new Image();
  img.src = pageData;

  // Descargar PDF
  const nombreArchivo = `Factura_${data.numeroFactura}_${data.placa}_${ancho}mm.pdf`;
  pdf.save(nombreArchivo);
}

export async function imprimirFactura(data: FacturaData, ancho: 50 | 80): Promise<void> {
  await generarFacturaPDF(data, ancho);
}

export const generateShiftClosePDF = (
  shift: ShiftDetails,
  declared: CloseShiftData,
  user?: { nombre: string },
  authorizingAdmin?: string | null
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  let y = 20;

  // Helper functions
  const centerText = (text: string, size: number = 10, isBold: boolean = false) => {
    doc.setFontSize(size);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    const textWidth = doc.getTextWidth(text);
    doc.text(text, (pageWidth - textWidth) / 2, y);
    y += size * 0.5 + 2;
  };

  const addLine = (label: string, value: string, yPos: number, isBold: boolean = false) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.text(label, margin, yPos);
    doc.text(value, pageWidth - margin - doc.getTextWidth(value), yPos);
  };

  const currency = (val: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(val);
  };

  // Header POINTER
  centerText('PARQIUM SYSTEMS', 16, true);
  centerText('COMPROBANTE DE CIERRE DE CAJA', 12, true);
  y += 5;
  centerText(`Turno: ${shift.numero}`, 10);
  centerText(`Fecha: ${new Date().toLocaleString('es-CO')}`, 10);
  y += 10;

  // Operator Info
  doc.setDrawColor(200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;
  addLine('Cajero/Operador:', user?.nombre || 'N/A', y);
  y += 6;
  if (authorizingAdmin) {
    doc.setTextColor(200, 80, 0);
    addLine('Autorizado por:', authorizingAdmin, y);
    doc.setTextColor(0, 0, 0);
    y += 6;
  }
  y += 2;

  // System Totals
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, y, pageWidth - (margin * 2), 8, 'F');
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('TOTALES SISTEMA', margin + 2, y);
  y += 10;

  addLine('Base Inicial:', currency(shift.montoInicial), y);
  y += 6;
  addLine('Ventas Efectivo:', currency(shift.totalEfectivo), y);
  y += 6;
  addLine('Ventas Tarjeta:', currency(shift.totalTarjeta), y);
  y += 6;
  addLine('Ventas Transferencia (Inc. Nequi):', currency(shift.totalTransferencia + shift.totalQr), y);
  // addLine('Ventas Nequi/Davi:', currency(shift.totalQr), y); // Merged above
  y += 8;
  doc.line(margin, y, pageWidth - margin, y); // Separator
  y += 8;
  addLine('TOTAL ESPERADO (Caja + Bancos):', currency(shift.totalEsperado + shift.totalTarjeta + shift.totalTransferencia + shift.totalQr), y, true);
  y += 10;

  // Declared Totals
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, y, pageWidth - (margin * 2), 8, 'F');
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('VALORES DECLARADOS', margin + 2, y);
  y += 10;

  addLine('Efectivo Contado:', currency(declared.efectivoContado), y);
  y += 6;
  addLine('Tarjetas (Vouchers):', currency(declared.tarjetaContada || 0), y);
  y += 6;
  addLine('Transferencias/Nequi:', currency((declared.transferenciaContada || 0) + (declared.qrContado || 0)), y);
  y += 8;
  addLine('TOTAL DECLARADO:', currency(declared.efectivoContado + (declared.tarjetaContada || 0) + (declared.transferenciaContada || 0) + (declared.qrContado || 0)), y, true);
  y += 10;

  // Differences
  const totalDiff = (declared.efectivoContado + (declared.tarjetaContada || 0) + (declared.transferenciaContada || 0) + (declared.qrContado || 0)) - (shift.totalEsperado + shift.totalTarjeta + shift.totalTransferencia + shift.totalQr);

  if (totalDiff !== 0) {
    doc.setTextColor(255, 0, 0);
    addLine('DIFERENCIA TOTAL:', currency(totalDiff), y, true);
    doc.setTextColor(0, 0, 0);
  } else {
    doc.setTextColor(0, 150, 0);
    addLine('ESTADO:', 'CUADRE PERFECTO', y, true);
    doc.setTextColor(0, 0, 0);
  }
  y += 15;

  // Observations
  if (declared.observaciones) {
    doc.setFont('helvetica', 'bold');
    doc.text('Observaciones / Justificación:', margin, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const splitObs = doc.splitTextToSize(declared.observaciones, pageWidth - (margin * 2));
    doc.text(splitObs, margin, y);
    y += (splitObs.length * 5) + 10;
  }

  // Signatures
  y = 250; // Bottom of page usually
  doc.line(margin, y, margin + 60, y);
  doc.line(pageWidth - margin - 60, y, pageWidth - margin, y);
  y += 5;
  doc.setFontSize(8);
  doc.text('Firma Cajero', margin, y);
  doc.text('Firma Supervisor', pageWidth - margin - 60, y);

  // Save
  doc.save(`Cierre_${shift.numero}.pdf`);
};

export const generateShiftCloseTicket = (
  shift: ShiftDetails,
  declared: CloseShiftData,
  user?: { nombre: string },
  options: { print?: boolean; download?: boolean } = { download: true },
  authorizingAdmin?: string | null
) => {
  const width = 80; // 80mm standard thermal
  const margin = 4;
  const contentWidth = width - (margin * 2);

  // Helper for currency formatting
  const currency = (val: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(val);
  };

  // Calculate total declared and total difference once
  const totalDeclared = declared.efectivoContado + (declared.tarjetaContada || 0) + (declared.transferenciaContada || 0) + (declared.qrContado || 0);
  const totalDiff = totalDeclared - (shift.totalEsperado + shift.totalTarjeta + shift.totalTransferencia + shift.totalQr);

  const drawContent = (d: jsPDF) => {
    let curY = margin;
    const cText = (txt: string, sz: number, bld: boolean) => {
      d.setFontSize(sz);
      d.setFont('helvetica', bld ? 'bold' : 'normal');
      const split = d.splitTextToSize(txt, contentWidth);
      split.forEach((l: string) => {
        const lw = d.getTextWidth(l);
        d.text(l, (width - lw) / 2, curY);
        curY += sz * 0.4 + 2;
      });
    };

    const aLine = (lbl: string, val: string, bld: boolean = false) => {
      d.setFontSize(8);
      d.setFont('helvetica', bld ? 'bold' : 'normal');
      const vw = d.getTextWidth(val);
      const lw = d.getTextWidth(lbl);
      if (lw + vw + 2 > contentWidth) {
        d.text(lbl, margin, curY);
        curY += 4;
        d.text(val, width - margin - vw, curY);
      } else {
        d.text(lbl, margin, curY);
        d.text(val, width - margin - vw, curY);
      }
      curY += 4;
    };

    const sep = () => {
      d.setLineWidth(0.1);
      d.line(margin, curY, width - margin, curY);
      curY += 3;
    };

    // DRAWING
    cText('PARQIUM SYSTEMS', 12, true);
    cText('CIERRE DE TURNO', 10, true);
    curY += 2;
    cText(`Turno #${shift.numero}`, 9, false);
    cText(new Date().toLocaleString('es-CO'), 8, false);
    curY += 3;
    sep();

    aLine('Cajero:', user?.nombre || 'N/A');
    if (authorizingAdmin) {
      d.setTextColor(200, 80, 0);
      aLine('Autorizó:', authorizingAdmin);
      d.setTextColor(0, 0, 0);
    }
    curY += 2;
    sep();

    cText('TOTALES SISTEMA', 9, true);
    curY += 2;
    aLine('Base Inicial:', currency(shift.montoInicial));
    aLine('Efectivo:', currency(shift.totalEfectivo));
    aLine('Tarjetas:', currency(shift.totalTarjeta));
    aLine('Transf/Nequi:', currency(shift.totalTransferencia + shift.totalQr)); // Merged
    // aLine('Nequi/Davi:', currency(shift.totalQr));
    curY += 2;
    aLine('ESPERADO:', currency(shift.totalEsperado + shift.totalTarjeta + shift.totalTransferencia + shift.totalQr), true);
    curY += 2;
    sep();

    cText('VALORES DECLARADOS', 9, true);
    curY += 2;
    aLine('Efectivo:', currency(declared.efectivoContado));
    aLine('Tarjetas:', currency(declared.tarjetaContada || 0));
    aLine('Trans/Nequi:', currency((declared.transferenciaContada || 0) + (declared.qrContado || 0)));
    curY += 2;
    aLine('DECLARADO:', currency(totalDeclared), true);
    curY += 2;
    sep();

    if (totalDiff !== 0) {
      cText('DIFERENCIAS', 9, true);
      aLine('Diferencia:', currency(totalDiff), true);
    } else {
      cText('*** CUADRE PERFECTO ***', 9, true);
    }
    curY += 3;

    if (declared.observaciones) {
      sep();
      cText('OBSERVACIONES', 9, true);
      d.setFont('helvetica', 'normal');
      d.setFontSize(8);
      const splitObs = d.splitTextToSize(declared.observaciones, contentWidth);
      d.text(splitObs, margin, curY);
      curY += (splitObs.length * 4) + 4;
    }

    curY += 8;
    cText('_______________________', 8, false);
    cText('Firma Cajero', 8, false);
    curY += 8;
    cText('_______________________', 8, false);
    cText('Firma Supervisor', 8, false);
    curY += 5;

    return curY;
  };

  // Create a temporary doc to measure content height
  const tempDoc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [width, 1000] // Long temporary height
  });
  tempDoc.setFont('helvetica');

  // Pass 1: Measure
  const height = drawContent(tempDoc);

  // Pass 2: Draw on the final PDF with correct height
  const finalPdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [width, height]
  });
  finalPdf.setFont('helvetica'); // Set font for final doc too

  drawContent(finalPdf);

  if (options.download) {
    finalPdf.save(`Ticket_Cierre_${shift.numero}.pdf`);
  }

  if (options.print) {
    finalPdf.autoPrint();
    const blob = finalPdf.output('bloburl');
    window.open(blob, '_blank');
  }
};
